import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Save, User, MapPin, Settings, ArrowLeft, UserPlus, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { useSupabaseData } from '@/contexts/SupabaseDataContext';

const StudentRegistration = ({ onNavigate }) => {
  const { toast } = useToast();
  const { addStudent } = useSupabaseData();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    student_name: '',
    responsible_name: '',
    location: '',
    age: '',
    bike_rim: ''
  });

  const polos = ['Osasco', 'Villa Lobos', 'Belenzinho', 'Guarulhos'];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.student_name || !formData.responsible_name || !formData.location || !formData.age || !formData.bike_rim) {
      toast({
        title: "Erro de Validação",
        description: "Por favor, preencha todos os campos obrigatórios.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    const newStudent = await addStudent(formData);
    setLoading(false);
    
    if (newStudent) {
      onNavigate('studentDashboard', newStudent);
    }
  };

  return (
    <div className="min-h-screen p-6 flex items-center justify-center">
      <div className="w-full max-w-lg mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            onClick={() => onNavigate('dashboard')}
            variant="ghost"
            className="text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar para o Dashboard
          </Button>
          <h1 className="text-4xl font-bold text-white mb-2 text-center">
            Registro de Aluno
          </h1>
          <p className="text-xl text-purple-300 text-center">
            Pedalando pela Inclusão
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="bg-transparent border-white/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <UserPlus className="mr-2 h-5 w-5" />
                Cadastrar Novo Aluno
              </CardTitle>
            </CardHeader>
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="student_name" className="text-white">
                      <User className="inline-block mr-2 h-4 w-4" />
                      Nome do Aluno *
                    </Label>
                    <Input
                      id="student_name"
                      type="text"
                      value={formData.student_name}
                      onChange={(e) => handleInputChange('student_name', e.target.value)}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                      placeholder="Digite o nome completo"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="responsible_name" className="text-white">
                      Responsável *
                    </Label>
                    <Input
                      id="responsible_name"
                      type="text"
                      value={formData.responsible_name}
                      onChange={(e) => handleInputChange('responsible_name', e.target.value)}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                      placeholder="Nome do responsável"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="age" className="text-white">
                      Idade *
                    </Label>
                    <Input
                      id="age"
                      type="number"
                      value={formData.age}
                      onChange={(e) => handleInputChange('age', e.target.value)}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                      placeholder="Digite a idade"
                      min="1"
                      max="100"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bike_rim" className="text-white flex items-center">
                      <Settings className="mr-1 h-4 w-4" />
                      Aro da Bicicleta *
                    </Label>
                    <Input
                      id="bike_rim"
                      type="text"
                      value={formData.bike_rim}
                      onChange={(e) => handleInputChange('bike_rim', e.target.value)}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                      placeholder="Ex: 20, 24"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="location" className="text-white">
                      Polo *
                    </Label>
                    <Select
                      id="location"
                      value={formData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      className="bg-white/10 border-white/20 text-white"
                    >
                      <option value="" disabled>Selecione o polo</option>
                      {polos.map(polo => (
                        <option key={polo} value={polo} className="bg-slate-800 text-white">
                          {polo}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-end">
                 <Button
                    type="submit"
                    disabled={loading}
                    className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white w-full"
                  >
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    {loading ? 'Registrando...' : 'Registrar e Continuar'}
                  </Button>
              </CardFooter>
            </form>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default StudentRegistration;